import { useRef, useState, useEffect, useId, useMemo } from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Icon } from '@/common/ui/Icon/Icon'
import { PlayCircleIcon, ArrowRightIcon } from '@/common/icons'
import { Modal } from '@/common/ui/Modal/Modal'
import { ScrollGradientBlock } from '@/common/ui/ScrollGradientBlock/ScrollGradientBlock'

import './mediaGallery.css'
import { Spinner } from '../Spinner'

interface MediaItem {
    type: 'image' | 'video'
    url: string | null
    originalIndex: number
    frames360?: string[]
    name?: string
    description?: string
    status?: 'failed' | 'completed' | 'pending' | 'running' | 'cancelled'
    /** Подпись типа генерации для пользователя (если передана — показывается в блоке «Тип») */
    displayGenerationType?: string
    /** Название изображения с сервера (если передано — показывается в блоке «Название изображения») */
    imageName?: string | null
    /** Текст на изображении; если поле передано (в т.ч. `null`) — показывается disabled textarea */
    textOnImage?: string | null
}

interface MediaGalleryProps {
    allMedia: MediaItem[]
    className?: string
    selectedMediaIndex: number
    onMediaSelect: (index: number) => void
    title?: string
    detailsContent?: React.ReactNode
    hidePreview?: boolean
    overflowScroll?: 'horizontal' | 'vertical'
    miniaturesPosition?: 'top' | 'bottom'
}

const MOBILE_PAGE_MAX_WIDTH = 768

const preloaded360FrameUrls = new Set<string>()
const loading360FrameUrls = new Map<string, Promise<void>>()

const preloadSingle360Frame = (frameUrl: string): Promise<void> => {
    if (!frameUrl || preloaded360FrameUrls.has(frameUrl)) {
        return Promise.resolve()
    }

    const existingPromise = loading360FrameUrls.get(frameUrl)
    if (existingPromise) {
        return existingPromise
    }

    const loadPromise = new Promise<void>(resolve => {
        const image = new Image()
        image.decoding = 'async'
        image.onload = () => {
            preloaded360FrameUrls.add(frameUrl)
            loading360FrameUrls.delete(frameUrl)
            resolve()
        }
        image.onerror = () => {
            loading360FrameUrls.delete(frameUrl)
            resolve()
        }
        image.src = frameUrl
    })

    loading360FrameUrls.set(frameUrl, loadPromise)
    return loadPromise
}

const preload360Frames = (frameUrls: string[]) => {
    frameUrls.forEach(frameUrl => {
        if (!frameUrl) {
            return
        }
        void preloadSingle360Frame(frameUrl)
    })
}

interface LazyThumbnailImageProps {
    src: string
    alt?: string
    containerRef: React.RefObject<HTMLDivElement | null>
    className?: string
}

const LazyThumbnailImage = ({ src, alt, containerRef, className }: LazyThumbnailImageProps) => {
    const imageWrapperRef = useRef<HTMLDivElement>(null)
    const [shouldLoad, setShouldLoad] = useState(false)

    useEffect(() => {
        if (shouldLoad) {
            return
        }

        const currentWrapper = imageWrapperRef.current
        if (!currentWrapper) {
            return
        }

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0]
                if (entry?.isIntersecting) {
                    setShouldLoad(true)
                    observer.disconnect()
                }
            },
            {
                root: containerRef.current,
                rootMargin: '120px',
            }
        )

        observer.observe(currentWrapper)

        return () => {
            observer.disconnect()
        }
    }, [containerRef, shouldLoad])

    return (
        <div ref={imageWrapperRef} className='w-full h-full'>
            {shouldLoad ? (
                <img
                    src={src}
                    alt={alt || 'Миниатюра изображения'}
                    loading='lazy'
                    decoding='async'
                    className={className}
                />
            ) : (
                <div className='w-full h-full bg-[rgba(217,217,217,0.12)]' />
            )}
        </div>
    )
}

interface Frame360ThumbnailProps {
    frames: string[]
    alt?: string
    containerRef: React.RefObject<HTMLDivElement | null>
    className?: string
}

const Frame360Thumbnail = ({ frames, alt, containerRef, className }: Frame360ThumbnailProps) => {
    const [currentFrameIndex, setCurrentFrameIndex] = useState(0)
    const [resolvedFrameUrl, setResolvedFrameUrl] = useState<string | null>(frames[0] || null)
    const thumbnailRafRef = useRef<number | null>(null)
    const latestFrameIndexRef = useRef(0)
    const desiredFrameUrlRef = useRef<string | null>(frames[0] || null)

    const handleThumbnailMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        if (frames.length <= 1) {
            return
        }

        if (thumbnailRafRef.current !== null) {
            return
        }

        const targetElement = event.currentTarget
        const clientX = event.clientX

        thumbnailRafRef.current = requestAnimationFrame(() => {
            const rect = targetElement.getBoundingClientRect()
            if (!rect.width) {
                thumbnailRafRef.current = null
                return
            }

            const normalizedX = Math.min(Math.max(clientX - rect.left, 0), rect.width - 1)
            const ratio = normalizedX / rect.width
            const nextFrameIndex = Math.min(Math.floor(ratio * frames.length), frames.length - 1)

            if (nextFrameIndex !== latestFrameIndexRef.current) {
                latestFrameIndexRef.current = nextFrameIndex
                setCurrentFrameIndex(nextFrameIndex)
            }

            thumbnailRafRef.current = null
        })
    }

    useEffect(() => {
        preload360Frames(frames)
    }, [frames])

    useEffect(() => {
        const nextFrameUrl = frames[currentFrameIndex] || null
        desiredFrameUrlRef.current = nextFrameUrl

        if (!nextFrameUrl) {
            setResolvedFrameUrl(null)
            return
        }

        if (preloaded360FrameUrls.has(nextFrameUrl)) {
            setResolvedFrameUrl(nextFrameUrl)
            return
        }

        void preloadSingle360Frame(nextFrameUrl).then(() => {
            if (desiredFrameUrlRef.current === nextFrameUrl) {
                setResolvedFrameUrl(nextFrameUrl)
            }
        })
    }, [currentFrameIndex, frames])

    useEffect(() => {
        setCurrentFrameIndex(0)
        latestFrameIndexRef.current = 0
        setResolvedFrameUrl(frames[0] || null)
        desiredFrameUrlRef.current = frames[0] || null
    }, [frames])

    useEffect(() => {
        return () => {
            if (thumbnailRafRef.current !== null) {
                cancelAnimationFrame(thumbnailRafRef.current)
            }
        }
    }, [])

    const desiredFrameUrl = frames[currentFrameIndex] || null
    const handleThumbnailMouseEnter = () => {
        preload360Frames(frames)
    }

    return (
        <div className='w-full h-full' onMouseMove={handleThumbnailMouseMove} onMouseEnter={handleThumbnailMouseEnter}>
            {resolvedFrameUrl && (
                <LazyThumbnailImage
                    src={resolvedFrameUrl}
                    alt={alt}
                    containerRef={containerRef}
                    className={className}
                />
            )}
            {desiredFrameUrl && desiredFrameUrl !== resolvedFrameUrl && (
                <img
                    src={desiredFrameUrl}
                    alt={alt || 'Следующий кадр 360'}
                    loading='eager'
                    decoding='async'
                    className='object-cover w-full h-full absolute inset-0 opacity-0 pointer-events-none'
                />
            )}
            <div className='absolute top-1 right-1 z-60 bg-black/50 rounded px-1 py-0.5 text-[10px] text-white'>
                360
            </div>
        </div>
    )
}

export const MediaGallery = ({
    allMedia,
    className,
    selectedMediaIndex,
    onMediaSelect,
    title,
    detailsContent,
    hidePreview = false,
    overflowScroll = 'vertical',
    miniaturesPosition = 'bottom',
}: MediaGalleryProps) => {
    const thumbnailsListRef = useRef<HTMLDivElement>(null)
    const thumbnailsContainerRef = useRef<HTMLDivElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const [hasMultipleRows, setHasMultipleRows] = useState(false)
    const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined)
    const [selected360FrameIndex, setSelected360FrameIndex] = useState(0)
    const [resolved360ImageUrl, setResolved360ImageUrl] = useState<string | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isMobileViewport, setIsMobileViewport] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth <= MOBILE_PAGE_MAX_WIDTH : false
    )
    const modalId = useId()
    const frameSwitchRafRef = useRef<number | null>(null)
    const latestFrameIndexRef = useRef<number>(0)
    const desiredImageUrlRef = useRef<string | null>(null)
    const currentMedia =
        selectedMediaIndex >= 0 && selectedMediaIndex < allMedia.length ? allMedia[selectedMediaIndex] : undefined
    const has360Frames = allMedia.some(media => (media.frames360?.length || 0) > 0)
    const currentMediaFrames360 = useMemo(() => currentMedia?.frames360 || [], [currentMedia])
    const isSelected360Frame = currentMediaFrames360.length > 1
    const desiredMediaImageUrl = isSelected360Frame
        ? currentMediaFrames360[selected360FrameIndex] || null
        : currentMedia?.url || null
    const selectedMediaImageUrl = isSelected360Frame ? resolved360ImageUrl : desiredMediaImageUrl
    const shouldShowMainUnavailableIcon = currentMedia?.status === 'failed'
    const effectiveOverflowScroll = isMobileViewport ? 'horizontal' : overflowScroll

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        const updateViewportState = () => {
            setIsMobileViewport(window.innerWidth <= MOBILE_PAGE_MAX_WIDTH)
        }

        updateViewportState()
        window.addEventListener('resize', updateViewportState)

        return () => {
            window.removeEventListener('resize', updateViewportState)
        }
    }, [])

    const handleMediaSelect = (index: number) => {
        if (onMediaSelect) {
            onMediaSelect(index)
            if (selectedMediaIndex === index) {
                setTimeout(() => {
                    const checkbox = document.getElementById(modalId) as HTMLInputElement
                    if (checkbox) {
                        checkbox.checked = true
                        setIsModalOpen(true)
                    }
                }, 100)
            }
        }
    }

    const handlePrevious = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (selectedMediaIndex > 0) {
            onMediaSelect(selectedMediaIndex - 1)
        }
        e.preventDefault()
    }

    const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (selectedMediaIndex >= 0 && selectedMediaIndex < allMedia.length - 1) {
            onMediaSelect(selectedMediaIndex + 1)
        }
        e.preventDefault()
    }

    const handleMainImageClick = () => {
        // Используем setTimeout чтобы убедиться, что Modal уже отрендерился через Portal
        setTimeout(() => {
            const checkbox = document.getElementById(modalId) as HTMLInputElement
            if (checkbox) {
                checkbox.checked = true
                setIsModalOpen(true)
            }
        }, 100)
    }

    const handleMainImageMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!isSelected360Frame || selectedMediaIndex < 0) {
            return
        }

        if (frameSwitchRafRef.current !== null) {
            return
        }

        const targetElement = event.currentTarget
        const clientX = event.clientX

        frameSwitchRafRef.current = requestAnimationFrame(() => {
            const rect = targetElement.getBoundingClientRect()
            if (!rect.width) {
                frameSwitchRafRef.current = null
                return
            }

            const normalizedX = Math.min(Math.max(clientX - rect.left, 0), rect.width - 1)
            const ratio = normalizedX / rect.width
            const nextFrameIndex = Math.min(
                Math.floor(ratio * currentMediaFrames360.length),
                currentMediaFrames360.length - 1
            )

            if (nextFrameIndex !== latestFrameIndexRef.current) {
                latestFrameIndexRef.current = nextFrameIndex
                setSelected360FrameIndex(nextFrameIndex)
            }

            frameSwitchRafRef.current = null
        })
    }

    const handleMainImageMouseEnter = () => {
        if (!isSelected360Frame) {
            return
        }
        preload360Frames(currentMediaFrames360)
    }

    const handleModalImageMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!isSelected360Frame || selectedMediaIndex < 0) {
            return
        }

        if (frameSwitchRafRef.current !== null) {
            return
        }

        const targetElement = event.currentTarget
        const clientX = event.clientX

        frameSwitchRafRef.current = requestAnimationFrame(() => {
            const rect = targetElement.getBoundingClientRect()
            if (!rect.width) {
                frameSwitchRafRef.current = null
                return
            }

            const normalizedX = Math.min(Math.max(clientX - rect.left, 0), rect.width - 1)
            const ratio = normalizedX / rect.width
            const nextFrameIndex = Math.min(
                Math.floor(ratio * currentMediaFrames360.length),
                currentMediaFrames360.length - 1
            )

            if (nextFrameIndex !== latestFrameIndexRef.current) {
                latestFrameIndexRef.current = nextFrameIndex
                setSelected360FrameIndex(nextFrameIndex)
            }

            frameSwitchRafRef.current = null
        })
    }

    const handleModalImageMouseEnter = () => {
        if (!isSelected360Frame) {
            return
        }
        preload360Frames(currentMediaFrames360)
    }

    useEffect(() => {
        setSelected360FrameIndex(0)
        latestFrameIndexRef.current = 0
    }, [selectedMediaIndex])

    useEffect(() => {
        desiredImageUrlRef.current = desiredMediaImageUrl

        if (!desiredMediaImageUrl) {
            setResolved360ImageUrl(null)
            return
        }

        if (!isSelected360Frame) {
            setResolved360ImageUrl(desiredMediaImageUrl)
            return
        }

        if (preloaded360FrameUrls.has(desiredMediaImageUrl)) {
            setResolved360ImageUrl(desiredMediaImageUrl)
            return
        }

        void preloadSingle360Frame(desiredMediaImageUrl).then(() => {
            if (desiredImageUrlRef.current === desiredMediaImageUrl) {
                setResolved360ImageUrl(desiredMediaImageUrl)
            }
        })
    }, [desiredMediaImageUrl, isSelected360Frame])

    useEffect(() => {
        if (!isSelected360Frame) {
            return
        }

        preload360Frames(currentMediaFrames360)
    }, [isSelected360Frame, currentMediaFrames360])

    useEffect(() => {
        const allFrames360 = allMedia.flatMap(media => media.frames360 || [])
        preload360Frames(allFrames360)
    }, [allMedia])

    useEffect(() => {
        const calculateRows = () => {
            if (!thumbnailsListRef.current || !thumbnailsContainerRef.current) {
                return
            }

            const listElement = thumbnailsListRef.current

            // Получаем первый элемент для вычисления высоты строки
            const firstButton = listElement.querySelector('button')
            if (!firstButton) {
                return
            }

            const computedStyle = window.getComputedStyle(listElement)
            const gap = effectiveOverflowScroll === 'vertical' ? parseFloat(computedStyle.gap) || 8 : 0
            const firstButtonRect = firstButton.getBoundingClientRect()
            const rowHeight = firstButtonRect.height + gap

            // Проверяем, есть ли больше одной строки
            const contentHeight = listElement.clientHeight

            const hasMultiple = contentHeight > rowHeight + 1
            setHasMultipleRows(hasMultiple)

            if (hasMultiple) {
                // Полторы строки
                setContainerHeight(rowHeight * 1.5)
            } else {
                // Одна строка
                setContainerHeight(rowHeight)
            }
        }

        // Вычисляем после рендера и при изменении количества медиа
        const timeoutId = setTimeout(calculateRows, 0)

        // Также пересчитываем при изменении размера окна
        window.addEventListener('resize', calculateRows)

        return () => {
            clearTimeout(timeoutId)
            window.removeEventListener('resize', calculateRows)
        }
    }, [allMedia.length, effectiveOverflowScroll])

    // Прокрутка к выбранному элементу
    useEffect(() => {
        if (!thumbnailsListRef.current || !thumbnailsContainerRef.current) {
            return
        }

        const listElement = thumbnailsListRef.current
        const containerElement = thumbnailsContainerRef.current
        const buttons = listElement.querySelectorAll('button')

        if (buttons.length === 0 || selectedMediaIndex < 0 || selectedMediaIndex >= allMedia.length) {
            return
        }

        // Если selectedMediaIndex = -1, не прокручиваем
        if (selectedMediaIndex < 0) {
            return
        }

        const selectedButton = buttons[selectedMediaIndex] as HTMLElement
        if (!selectedButton) {
            return
        }

        // Вычисляем позицию для прокрутки контейнера
        const timeoutId = setTimeout(() => {
            // Позиция элемента относительно списка
            const buttonTop = selectedButton.offsetTop
            const buttonHeight = selectedButton.offsetHeight
            const containerScrollTop = containerElement.scrollTop
            const containerHeight = containerElement.clientHeight

            // Проверяем, виден ли элемент
            const elementTopRelativeToContainer = buttonTop - containerScrollTop
            const elementBottomRelativeToContainer = elementTopRelativeToContainer + buttonHeight

            // Прокручиваем, если элемент не полностью виден
            if (elementTopRelativeToContainer < 0) {
                // Элемент выше видимой области
                containerElement.scrollTo({
                    top: buttonTop,
                    behavior: 'smooth',
                })
            } else if (elementBottomRelativeToContainer > containerHeight) {
                // Элемент ниже видимой области
                containerElement.scrollTo({
                    top: buttonTop + buttonHeight - containerHeight,
                    behavior: 'smooth',
                })
            }
        }, 100)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [selectedMediaIndex, allMedia.length])

    // Остановка видео при закрытии модального окна и обработка клавиатуры
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                const checkbox = document.getElementById(modalId) as HTMLInputElement
                if (checkbox && checkbox.checked) {
                    checkbox.checked = false
                    setIsModalOpen(false)
                }
                return
            }

            if (!isModalOpen) {
                return
            }

            if (e.key === 'ArrowLeft') {
                if (selectedMediaIndex > 0) {
                    e.preventDefault()
                    onMediaSelect(selectedMediaIndex - 1)
                }
            } else if (e.key === 'ArrowRight') {
                if (selectedMediaIndex >= 0 && selectedMediaIndex < allMedia.length - 1) {
                    e.preventDefault()
                    onMediaSelect(selectedMediaIndex + 1)
                }
            }
        }

        const handleCheckboxChange = (e: Event) => {
            const target = e.target as HTMLInputElement
            setIsModalOpen(target.checked)
            if (!target.checked && videoRef.current) {
                videoRef.current.pause()
                videoRef.current.currentTime = 0
            }
        }

        // Подписываемся на события после небольшой задержки, чтобы checkbox успел отрендериться через Portal
        const timeoutId = setTimeout(() => {
            const checkbox = document.getElementById(modalId) as HTMLInputElement
            if (checkbox) {
                checkbox.addEventListener('change', handleCheckboxChange)
            }
        }, 200)

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            clearTimeout(timeoutId)
            const checkbox = document.getElementById(modalId) as HTMLInputElement
            if (checkbox) {
                checkbox.removeEventListener('change', handleCheckboxChange)
            }
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [modalId, currentMedia, isModalOpen, selectedMediaIndex, allMedia.length, onMediaSelect])

    useEffect(() => {
        return () => {
            if (frameSwitchRafRef.current !== null) {
                cancelAnimationFrame(frameSwitchRafRef.current)
            }
        }
    }, [])

    const videosCount = allMedia.filter(m => m.type === 'video').length
    const imagesCount = allMedia.filter(m => m.type === 'image').length

    const miniatures = () => {
        return (
            <>
                {allMedia.length > 1 && (
                    <ScrollGradientBlock
                        ref={thumbnailsContainerRef}
                        enabled={hasMultipleRows && effectiveOverflowScroll === 'vertical'}
                        className='relative w-full'
                        scrollContainerClassName='relative w-full thumbnails-container'
                        scrollContainerStyle={{
                            maxHeight: containerHeight ? `${containerHeight}px` : undefined,
                        }}
                    >
                        <div ref={thumbnailsListRef} className='thumbnails-list'>
                            {allMedia.map((media, index) => {
                                const shouldShowUnavailableIcon = media.status === 'failed'

                                return (
                                    <button
                                        key={index}
                                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                            handleMediaSelect(index)
                                            e.preventDefault()
                                        }}
                                        className={cn(
                                            'relative rounded-lg overflow-hidden cursor-pointer transition-all',
                                            selectedMediaIndex === index
                                                ? 'border-2 border-[#be96ff]'
                                                : 'border-2 border-transparent hover:border-[#be96ff]/30'
                                        )}
                                        style={{
                                            background: 'rgba(217, 217, 217, 0.01)',
                                        }}
                                    >
                                        {shouldShowUnavailableIcon ? (
                                            <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 pointer-events-none'>
                                                <Icon name='cross' className='w-12 h-12' />
                                            </div>
                                        ) : media.type === 'video' ? (
                                            <>
                                                <video
                                                    src={media.url || ''}
                                                    className='object-cover w-full h-full'
                                                    controls={false}
                                                    autoPlay={false}
                                                    preload='metadata'
                                                />
                                                <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 pointer-events-none'>
                                                    <PlayCircleIcon />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <Spinner className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 pointer-events-none' />
                                                {media.frames360 && media.frames360.length > 0 && (
                                                    <Frame360Thumbnail
                                                        frames={media.frames360}
                                                        alt={media.name}
                                                        containerRef={thumbnailsContainerRef}
                                                        className='object-cover w-full h-full absolute top-0'
                                                    />
                                                )}
                                                {!media.frames360 && media.url && (
                                                    <LazyThumbnailImage
                                                        src={media.url}
                                                        alt={media.name}
                                                        containerRef={thumbnailsContainerRef}
                                                        className='object-cover w-full h-full absolute top-0'
                                                    />
                                                )}
                                            </>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </ScrollGradientBlock>
                )}
            </>
        )
    }

    return (
        <div className={cn(`media-gallery overflowScroll-${effectiveOverflowScroll}`, className)}>
            {title && (
                <div className='flex items-center justify-between relative w-full'>
                    <h3 className='font-medium leading-[22px] relative text-lg text-white'>{title}</h3>

                    <div className='flex gap-2 items-center justify-end relative'>
                        {has360Frames && (
                            <div className='flex gap-1.5 items-center relative' title='Количество 360° изображений'>
                                1
                                <span className='font-normal relative text-sm text-white whitespace-nowrap border rounded-[4px] px-[2px] py-[0.5px] text-[9px]'>
                                    360°
                                </span>
                            </div>
                        )}
                        {videosCount > 0 && (
                            <div className='flex gap-1.5 items-center relative' title='Количество видео'>
                                <span className='font-normal relative text-sm text-white whitespace-nowrap'>
                                    {videosCount}
                                </span>
                                <Icon name='video-square' className='w-[18px] h-[18px]' />
                            </div>
                        )}
                        {imagesCount > 0 && (
                            <div className='flex gap-1.5 items-center relative' title='Количество изображений'>
                                <span className='font-normal relative text-sm text-white whitespace-nowrap'>
                                    {imagesCount}
                                </span>
                                <Icon name='gallery' className='w-[18px] h-[18px]' />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Галерея с превью */}
            {allMedia.length > 0 && (
                <div className='flex flex-col gap-4 items-center relative w-full'>
                    {miniaturesPosition === 'top' && miniatures()}
                    {/* Основное изображение с навигацией - скрываем если hidePreview */}
                    {!hidePreview && currentMedia && (
                        <div className='flex items-center justify-between relative w-full'>
                            {/* Стрелка влево */}
                            {allMedia.length > 1 && (
                                <button
                                    onClick={handlePrevious}
                                    disabled={selectedMediaIndex <= 0}
                                    className='flex cursor-pointer items-center justify-center h-10 px-2.5 py-0 relative disabled:opacity-50 disabled:cursor-not-allowed'
                                    aria-label='Предыдущее изображение'
                                >
                                    <div className='rotate-180'>
                                        <ArrowRightIcon />
                                    </div>
                                </button>
                            )}

                            {/* Основное изображение и описание */}
                            <div className='flex gap-4 items-center justify-center relative w-full'>
                                <div
                                    className='bg-[rgba(217,217,217,0.1)] min-h-[240px] relative rounded-lg shrink-0 w-[180px] overflow-hidden cursor-pointer flex items-center'
                                    onClick={handleMainImageClick}
                                    onMouseEnter={handleMainImageMouseEnter}
                                    role='button'
                                    tabIndex={0}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            handleMainImageClick()
                                        }
                                    }}
                                    onMouseMove={handleMainImageMouseMove}
                                    aria-label='Открыть в полном размере'
                                >
                                    {shouldShowMainUnavailableIcon ? (
                                        <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 pointer-events-none'>
                                            <Icon name='cross' className='w-12 h-12' />
                                        </div>
                                    ) : (
                                        <Spinner className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 pointer-events-none' />
                                    )}
                                    {!shouldShowMainUnavailableIcon &&
                                    currentMedia?.type === 'video' &&
                                    currentMedia?.url ? (
                                        <div className='relative'>
                                            <video
                                                src={currentMedia.url}
                                                className='object-cover w-full h-full'
                                                controls={false}
                                                autoPlay={false}
                                                preload='metadata'
                                            />
                                            <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[52px] h-[52px] pointer-events-none '>
                                                <PlayCircleIcon />
                                            </div>
                                        </div>
                                    ) : !shouldShowMainUnavailableIcon ? (
                                        selectedMediaImageUrl && (
                                            <>
                                                <img
                                                    src={selectedMediaImageUrl}
                                                    alt={currentMedia.name || 'Выбранное изображение'}
                                                    loading='eager'
                                                    decoding='async'
                                                    className='object-contain w-full h-full rounded-lg relative'
                                                />
                                                {isSelected360Frame &&
                                                    desiredMediaImageUrl &&
                                                    desiredMediaImageUrl !== selectedMediaImageUrl && (
                                                        <img
                                                            src={desiredMediaImageUrl}
                                                            alt='Следующий кадр 360'
                                                            loading='eager'
                                                            decoding='async'
                                                            className='object-cover w-full h-full absolute inset-0 opacity-0 pointer-events-none'
                                                        />
                                                    )}
                                            </>
                                        )
                                    ) : null}
                                </div>

                                {detailsContent ?? (
                                    <div className='flex flex-col gap-3 flex-1 w-full items-stretch justify-start relative shrink-0'>
                                        <div className='flex flex-col font-normal justify-center leading-[0] relative shrink-0 text-sm text-[var(--pixorion-gray-2)]'>
                                            <p className='leading-[1.4] text-nowrap'>
                                                {selectedMediaIndex >= 0 ? selectedMediaIndex + 1 : 0} /{' '}
                                                {allMedia.length}
                                            </p>
                                        </div>
                                        {currentMedia?.displayGenerationType?.trim() && (
                                            <div className='flex flex-col gap-1 w-full min-w-0'>
                                                <span className='text-xs font-normal leading-[1.4] text-[var(--pixorion-gray-2)]'>
                                                    Тип
                                                </span>
                                                <p className='font-medium leading-[1.4] text-base text-white whitespace-normal break-words'>
                                                    {currentMedia.displayGenerationType.trim()}{' '}
                                                    {currentMedia?.imageName?.trim() && ' / '}
                                                    {currentMedia?.imageName?.trim()}
                                                </p>
                                            </div>
                                        )}
                                        {currentMedia?.name && (
                                            <p className='font-medium leading-[1.4] relative shrink-0 text-lg text-white'>
                                                {currentMedia?.name}
                                            </p>
                                        )}
                                        {currentMedia?.description && (
                                            <p className='font-medium leading-[1.4] relative shrink-0 text-lg text-white'>
                                                {currentMedia?.description}
                                            </p>
                                        )}
                                        {currentMedia && currentMedia.textOnImage !== undefined && (
                                            <div className='flex flex-col gap-1 w-full min-w-0'>
                                                <label
                                                    htmlFor={`${modalId}-text-on-image`}
                                                    className='text-xs font-normal leading-[1.4] text-[var(--pixorion-gray-2)]'
                                                >
                                                    Текст на изображении
                                                </label>
                                                <textarea
                                                    id={`${modalId}-text-on-image`}
                                                    disabled
                                                    value={currentMedia.textOnImage ?? ''}
                                                    rows={4}
                                                    aria-label='Текст на изображении'
                                                    className='w-full min-h-[88px] resize-none rounded-md border border-white/10 bg-black/25 px-2 py-1.5 text-sm leading-[1.4] text-white/90 placeholder:text-white/30 disabled:cursor-not-allowed disabled:opacity-90'
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Стрелка вправо */}
                            {allMedia.length > 1 && (
                                <button
                                    onClick={handleNext}
                                    disabled={selectedMediaIndex < 0 || selectedMediaIndex >= allMedia.length - 1}
                                    className='flex cursor-pointer items-center justify-center h-10 px-2.5 py-0 relative disabled:opacity-50 disabled:cursor-not-allowed'
                                    aria-label='Следующее изображение'
                                >
                                    <ArrowRightIcon />
                                </button>
                            )}
                        </div>
                    )}
                    {miniaturesPosition === 'bottom' && miniatures()}
                </div>
            )}

            {allMedia.length === 0 && <div className='w-full text-[#aba7af]'>Медиа файлы отсутствуют</div>}

            {/* Модальное окно для предпросмотра */}
            {currentMedia && selectedMediaImageUrl && (
                <Modal id={modalId} color='transparent' className='media-preview-modal'>
                    <div
                        className='group relative flex items-center justify-center w-full h-full max-w-[90vw] max-h-[90vh]'
                        onMouseMove={handleModalImageMouseMove}
                        onMouseEnter={handleModalImageMouseEnter}
                    >
                        {allMedia.length > 1 && (
                            <button
                                type='button'
                                onClick={e => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (selectedMediaIndex > 0) {
                                        onMediaSelect(selectedMediaIndex - 1)
                                    }
                                }}
                                disabled={selectedMediaIndex <= 0}
                                className='absolute left-2 top-1/2 -translate-y-1/2 z-[70] flex items-center justify-center w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white cursor-pointer opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 disabled:opacity-0 disabled:group-hover:opacity-30 disabled:group-focus-within:opacity-30 disabled:cursor-not-allowed transition-colors'
                                aria-label='Предыдущее изображение'
                            >
                                <div className='rotate-180'>
                                    <ArrowRightIcon />
                                </div>
                            </button>
                        )}
                        {allMedia.length > 1 && (
                            <button
                                type='button'
                                onClick={e => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (selectedMediaIndex < allMedia.length - 1) {
                                        onMediaSelect(selectedMediaIndex + 1)
                                    }
                                }}
                                disabled={selectedMediaIndex >= allMedia.length - 1}
                                className='absolute right-2 top-1/2 -translate-y-1/2 z-[70] flex items-center justify-center w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white cursor-pointer opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 disabled:opacity-0 disabled:group-hover:opacity-30 disabled:group-focus-within:opacity-30 disabled:cursor-not-allowed transition-colors'
                                aria-label='Следующее изображение'
                            >
                                <ArrowRightIcon />
                            </button>
                        )}
                        {currentMedia.type === 'video' ? (
                            <video
                                ref={videoRef}
                                src={currentMedia.url || ''}
                                className='max-w-full max-h-[90vh] object-contain rounded-lg'
                                controls
                                autoPlay={false}
                                preload='metadata'
                            />
                        ) : (
                            <div className='relative max-w-full max-h-[90vh] w-full h-full flex items-center justify-center'>
                                <img
                                    src={selectedMediaImageUrl}
                                    alt='Предпросмотр изображения'
                                    loading='eager'
                                    decoding='async'
                                    className='max-w-full max-h-[90vh] object-contain rounded-lg'
                                />
                                {isSelected360Frame &&
                                    desiredMediaImageUrl &&
                                    desiredMediaImageUrl !== selectedMediaImageUrl && (
                                        <img
                                            src={desiredMediaImageUrl}
                                            alt='Следующий кадр 360'
                                            loading='eager'
                                            decoding='async'
                                            className='max-w-full max-h-[90vh] object-contain rounded-lg absolute inset-0 m-auto opacity-0 pointer-events-none'
                                        />
                                    )}
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    )
}
